import api from '../axiosConfig';
import { HOTELS, BASE_URL_PMS } from '../config/apiEndpoints';

const inFlightHotelRequests = new Map();

export const HotelService = {
    /**
     * list — Fetches the full hotel property list for dropdowns/switchers.
     * GET /api/v1/hotels — no pagination or search params.
     * x-tenant-id header is automatically attached by the axios interceptor.
     */
    list: async () => {
        try {
            const response = await api.get(HOTELS.BASE);
            return response.data;
        } catch (error) {
            console.error('HotelService list error:', error);
            throw error;
        }
    },

    getAll: async (params = {}) => {
        try {
            if (!BASE_URL_PMS) {
                console.error('REACT_APP_API_BASE_URL_PMS is not defined in environment variables');
                throw new Error('API Configuration Error: PMS Base URL is missing');
            }

            const axiosParams = {};
            if (params.search) axiosParams.search = params.search;
            if (params.page !== undefined) axiosParams.page = params.page + 1;
            if (params.limit) axiosParams.limit = params.limit;
            if (params.noPagination) axiosParams.noPagination = true;
            if (params.noPaging) axiosParams.noPaging = true;
            if (params.status && params.status !== 'All') {
                if (params.status === 'ARCHIVED') {
                    axiosParams.showArchived = 'true';
                    axiosParams.status = 'Archived';
                } else {
                    axiosParams.status = params.status;
                }
            }

            const response = await api.get(HOTELS.BASE, { params: axiosParams });
            return response.data;
        } catch (error) {
            console.error('HotelService getAll error:', error);
            throw error;
        }
    },

    getById: async (id, tenantId) => {
        try {
            if (!id || id === 'null' || id === 'undefined') {
                return null;
            }
            const cacheKey = `${id}_${tenantId || ''}`;
            if (inFlightHotelRequests.has(cacheKey)) {
                return await inFlightHotelRequests.get(cacheKey);
            }

            const fetchPromise = (async () => {
                const url = HOTELS.BY_ID(id);
                const headers = {};
                if (tenantId) {
                    headers['x-tenant-id'] = String(tenantId);
                }
                const response = await api.get(url, { headers });
                return response.data;
            })();

            inFlightHotelRequests.set(cacheKey, fetchPromise);

            fetchPromise.finally(() => {
                setTimeout(() => {
                    inFlightHotelRequests.delete(cacheKey);
                }, 2000);
            });

            return await fetchPromise;
        } catch (error) {
            console.error('HotelService getById error:', error);
            throw error;
        }
    },

    save: async (hotel) => {
        try {
            const isEditing = !!hotel.id;
            const url = isEditing ? HOTELS.BY_ID(hotel.id) : HOTELS.BASE;

            console.log(`[HotelService] ${isEditing ? 'PUT' : 'POST'} ${url}`, hotel);

            // When Super Admin is in global context (x-tenant-id: null),
            // the PMS API rejects write operations with 401.
            // Override the header with the tenantId from the payload body.
            const headers = {};
            if (hotel.tenantId) {
                headers['x-tenant-id'] = String(hotel.tenantId);
            }

            const response = isEditing
                ? await api.put(url, hotel, { headers })
                : await api.post(url, hotel, { headers });

            return response.data;
        } catch (error) {
            console.error('HotelService save error:', error);
            throw error;
        }
    },

    changeStatus: async (id, tenantId, status, reason) => {
        try {
            const url = HOTELS.STATUS(id);
            const headers = {};
            if (tenantId) {
                headers['x-tenant-id'] = String(tenantId);
            }
            const response = await api.post(url, { status, reason }, { headers });
            return response.data;
        } catch (error) {
            console.error('HotelService changeStatus error:', error);
            throw error;
        }
    },

    delete: async (id) => {
        try {
            const response = await api.delete(HOTELS.BY_ID(id));
            return response.data;
        } catch (error) {
            console.error('HotelService delete error:', error);
            throw error;
        }
    },

    archive: async (id, tenantId, reason, notes) => {
        try {
            const url = HOTELS.ARCHIVE(id);
            const headers = {};
            if (tenantId) {
                headers['x-tenant-id'] = String(tenantId);
            }
            const response = await api.post(url, { reason, notes }, { headers });
            return response.data;
        } catch (error) {
            console.error('HotelService archive error:', error);
            throw error;
        }
    },

    restore: async (id, tenantId) => {
        try {
            const url = HOTELS.RESTORE(id);
            const headers = {};
            if (tenantId) {
                headers['x-tenant-id'] = String(tenantId);
            }
            const response = await api.post(url, {}, { headers });
            return response.data;
        } catch (error) {
            console.error('HotelService restore error:', error);
            throw error;
        }
    },

    manageAdmin: async (id, tenantId, payload) => {
        try {
            const url = HOTELS.MANAGE_ADMIN(id);
            const headers = {};
            if (tenantId) {
                headers['x-tenant-id'] = String(tenantId);
            }
            const response = await api.post(url, payload, { headers });
            return response.data;
        } catch (error) {
            console.error('HotelService manageAdmin error:', error);
            throw error;
        }
    },

    exportHotels: async (tenantId, format = 'csv') => {
        try {
            const url = HOTELS.EXPORT;
            const headers = {};
            if (tenantId) {
                headers['x-tenant-id'] = String(tenantId);
            }
            const response = await api.get(url, { params: { format }, headers, responseType: 'blob' });
            return response;
        } catch (error) {
            console.error('HotelService exportHotels error:', error);
            throw error;
        }
    },

    getAuditLogs: async (id, tenantId) => {
        try {
            const url = HOTELS.AUDIT_LOGS(id);
            const headers = {};
            if (tenantId) {
                headers['x-tenant-id'] = String(tenantId);
            }
            const response = await api.get(url, { headers });
            return response.data;
        } catch (error) {
            console.error('HotelService getAuditLogs error:', error);
            throw error;
        }
    },

    cloneHotelSettings: async (tenantId, payload) => {
        try {
            const url = HOTELS.CLONE;
            const headers = {};
            if (tenantId) {
                headers['x-tenant-id'] = String(tenantId);
            }
            const response = await api.post(url, payload, { headers });
            return response.data;
        } catch (error) {
            console.error('HotelService cloneHotelSettings error:', error);
            throw error;
        }
    }
};
